import { useEffect, useRef, useState } from "react";

import useAxios from "@hooks/use-axios";
import { CURRENCY } from "@src/utility/constants/panel/common";

const DEFAULT_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

const useBrandPublishers = (brandId) => {

    const { axiosInstance, loading } = useAxios({ base: "brand", initialLoader: true });
    const [publishers, setPublishers] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });
    const [stats, setStats] = useState({ total_publishers: 0, kyc_verified: 0, total_pending: 0 });
    const [search, setSearch] = useState("");
    const isFirstRender = useRef(true);

    const fetchPublishers = async (page = 1, limit = DEFAULT_LIMIT, currentSearch = search) => {
        if (!brandId) return;
        try {
            const res = await axiosInstance.get(`v1/accounts/${brandId}/affiliates`, {
                params: { search: currentSearch || undefined, page, limit }
            });
            const result = res.data || res;
            const list = result.items || result.data || result.results || [];
            setPublishers(list);
            setPagination({
                total: result.total ?? list.length,
                page : result.page ?? page,
                limit: result.limit ?? limit
            });
            setStats(result.stats || { total_publishers: list.length, kyc_verified: 0, total_pending: 0 });
        } catch {
            setPublishers([]);
            setPagination({ total: 0, page, limit });
        }
    };

    useEffect(() => {
        fetchPublishers(1, DEFAULT_LIMIT, search);
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const timer = setTimeout(() => fetchPublishers(1, pagination.limit, search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchPublishers(page, limit, search);
    const handlePerRowsChange = (limit) => fetchPublishers(1, limit, search);

    return {
        loading,
        currency       : CURRENCY,
        publishers,
        pagination,
        search,
        setSearch,
        handlePageChange,
        handlePerRowsChange,
        totalPending   : stats.total_pending,
        verifiedCount  : stats.kyc_verified,
        totalPublishers: stats.total_publishers
    };
};

export default useBrandPublishers;
